import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { STATS, FEATURES, TESTIMONIALS, SPECS } from "@/lib/index";
import { Star, ChevronDown, Zap, Globe2, ShieldCheck, Users } from "lucide-react";

// ---------- 官方图片 CDN ----------
const CDN = "https://static.rokidcdn.com/prod/rokid-website-glasses/2.1.1/_next/static/media";
const IMGS = {
  poster:   `${CDN}/poster.b0870333.png`,
  poster2:  `${CDN}/poster.84c909e5.png`,
  glasses:  `${CDN}/glasses.219d2675.png`,
  bolon:    `${CDN}/bolonImg.23f9e0f5.jpg`,
  thumb1:   `${CDN}/thumb1.bc7b40e8.png`,
  thumb2:   `${CDN}/thumb2.accf6746.png`,
  thumb3:   `${CDN}/thumb3.cfcf61fb.png`,
  main:     `${CDN}/main.ed5d6b8f.png`,
  ai1:      `${CDN}/1.d7594329.jpg`,
  ai2:      `${CDN}/2.95c29cc0.jpg`,
  ai3:      `${CDN}/3.5a9e0866.jpg`,
  ai4:      `${CDN}/4.7a02dc9e.jpg`,
  ai5:      `${CDN}/5.fdda40b8.jpg`,
  lifestyle1:`${CDN}/1.f4346903.jpg`,
  lifestyle2:`${CDN}/2.dffc12bf.jpg`,
  lifestyle3:`${CDN}/3.38738d04.jpg`,
  shop:     `${CDN}/shop.e5088bef.jpg`,
  box:      `${CDN}/box_img.db976867.png`,
  app:      `${CDN}/app_img.b25a2f4f.png`,
};

// ---------- Animation helpers ----------
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

/** Scroll-triggered section wrapper */
function Section({
  children,
  className = "",
  id,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.section
      id={id}
      ref={ref}
      variants={staggerContainer}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={className}
      style={style}
    >
      {children}
    </motion.section>
  );
}

// ---------- CSS keyframes injected once ----------
const STYLE_TAG = `
@keyframes breathe {
  0%,100% { opacity:0.55; text-shadow: 0 0 6px #40FF5E88; }
  50%      { opacity:1;    text-shadow: 0 0 18px #40FF5Ecc, 0 0 32px #40FF5E66; }
}
@keyframes scanline {
  0%   { transform: translateY(-100%); opacity:0.6; }
  100% { transform: translateY(200%);  opacity:0; }
}
@keyframes rimPulse {
  0%,100% { box-shadow: 0 0 0 1px rgba(64,255,94,.2), 0 0 12px rgba(64,255,94,.12); }
  50%     { box-shadow: 0 0 0 1.5px rgba(64,255,94,.55), 0 0 28px rgba(64,255,94,.32); }
}
@keyframes cursorBlink {
  0%,100% { opacity:1; }
  50%     { opacity:0; }
}
@keyframes floatY {
  0%,100% { transform: translateY(0px); }
  50%     { transform: translateY(-10px); }
}
@keyframes glowPulse {
  0%,100% { opacity:.15; }
  50%     { opacity:.35; }
}
.ar-text   { animation: breathe 2.4s ease-in-out infinite; }
.scanline  { animation: scanline 3s linear infinite; }
.rim-pulse { animation: rimPulse 2.8s ease-in-out infinite; }
.cursor-blink { animation: cursorBlink 0.9s step-end infinite; }
.float-y   { animation: floatY 4s ease-in-out infinite; }
.glow-pulse{ animation: glowPulse 3s ease-in-out infinite; }
`;

function GlobalStyle() {
  return <style dangerouslySetInnerHTML={{ __html: STYLE_TAG }} />;
}

// ---------- Animated counter ----------
function Counter({ to, suffix = "" }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView || !ref.current) return;
    const ctrl = animate(0, to, {
      duration: 1.6,
      ease: "easeOut",
      onUpdate(v) {
        if (ref.current) ref.current.textContent = Math.round(v) + suffix;
      },
    });
    return () => ctrl.stop();
  }, [inView, to, suffix]);
  return <span ref={ref}>0{suffix}</span>;
}

// ================================================
// HERO
// ================================================
function Hero() {
  const [typed, setTyped] = useState("");
  const words = ["实时翻译", "智能导航", "提词助演", "第一视角", "AI 助理"];
  const [wordIdx, setWordIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let i = 0;
    let cancelled = false;
    const word = words[wordIdx];

    const type = () => {
      if (cancelled) return;
      if (i <= word.length) {
        setTyped(word.slice(0, i));
        i++;
        timerRef.current = setTimeout(type, 110);
      } else {
        timerRef.current = setTimeout(() => {
          const erase = () => {
            if (cancelled) return;
            if (i >= 0) {
              setTyped(word.slice(0, i));
              i--;
              timerRef.current = setTimeout(erase, 55);
            } else {
              setWordIdx((prev) => (prev + 1) % words.length);
            }
          };
          erase();
        }, 1800);
      }
    };
    type();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordIdx]);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--primary) 14%, transparent), transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, color-mix(in srgb, var(--accent) 8%, transparent), transparent 60%), var(--background)",
      }}
    >
      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Ambient glows */}
      <div className="glow-pulse absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ background: "var(--primary)" }} />
      <div className="glow-pulse absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none"
        style={{ background: "var(--accent)", animationDelay: "1.4s" }} />

      <div className="relative z-10 max-w-5xl mx-auto w-full">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-8 border"
          style={{
            background: "color-mix(in srgb, var(--primary) 10%, transparent)",
            borderColor: "color-mix(in srgb, var(--primary) 30%, transparent)",
            color: "var(--primary)",
          }}
        >
          <Zap size={12} />
          全球10万+用户 · 双11智能眼镜品类销量第一
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-5xl md:text-7xl font-extrabold leading-tight mb-4"
          style={{ textWrap: "balance" } as React.CSSProperties}
        >
          戴上眼镜
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, var(--primary), var(--accent))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {typed}
            <span
              className="cursor-blink"
              style={{ WebkitTextFillColor: "var(--primary)", color: "var(--primary)" }}
            >|</span>
          </span>
          <br />
          就这么简单
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Rokid 乐奇 AI 智能眼镜 — 集实时翻译、智能导航、提词演讲、第一视角拍摄与 AI 助理于一体，
          让科技融入日常，让生活更从容。
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
        >
          <button
            onClick={() => document.getElementById("buy")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-4 rounded-full font-bold text-base text-primary-foreground transition-all duration-300 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
              boxShadow: "0 8px 30px color-mix(in srgb, var(--primary) 45%, transparent)",
            }}
          >
            立即体验 → ¥3299起
          </button>
          <button
            onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-4 rounded-full font-semibold text-sm border border-border hover:border-primary transition-all duration-200 text-foreground hover:text-primary"
          >
            了解功能特色
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl md:text-4xl font-black mb-1"
                style={{
                  background: "linear-gradient(135deg, var(--primary), var(--accent))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                <Counter to={Number(stat.value)} suffix={stat.suffix ?? ""} />
              </div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-muted-foreground"
      >
        <span className="text-xs">向下滚动</span>
        <ChevronDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

// ================================================
// GLASSES SHOWCASE — 官方产品图 + AR 呼吸动效
// ================================================
function GlassesShowcase() {
  return (
    <section
      className="py-20 px-6 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 100% 80% at 50% 50%, color-mix(in srgb, var(--primary) 7%, transparent), transparent 80%), var(--muted)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4">
            看起来和普通眼镜一样
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, var(--primary), var(--accent))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              但能做到的远不止这些
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            外观时尚，仅 49g 轻量佩戴；内里藏着一整套 AI 超能力。
            它始终在你眼前，静静地帮你解决一切。
          </p>
        </motion.div>

        {/* Main product image with AR overlay */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left: hero product image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="relative float-y"
          >
            <div
              className="rim-pulse relative rounded-3xl overflow-hidden"
              style={{
                background: "linear-gradient(145deg, var(--card), #0a0a0a)",
              }}
            >
              {/* Scanline effect */}
              <div
                className="scanline absolute inset-x-0 h-16 pointer-events-none z-10"
                style={{
                  background: "linear-gradient(180deg, transparent, rgba(64,255,94,0.08), transparent)",
                }}
              />
              <img
                src={IMGS.poster}
                alt="乐奇AI眼镜产品"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              {/* Green tint overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(to top, rgba(64,255,94,0.07) 0%, transparent 60%)",
                }}
              />
            </div>

            {/* Badge */}
            <div
              className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full text-xs font-bold border"
              style={{
                background: "var(--card)",
                borderColor: "var(--primary)",
                color: "var(--primary)",
                boxShadow: "0 4px 14px color-mix(in srgb, var(--primary) 35%, transparent)",
              }}
            >
              AR 显示
            </div>
          </motion.div>

          {/* Right: AR HUD mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            viewport={{ once: true }}
            className="flex flex-col gap-5"
          >
            {/* AR HUD Card */}
            <div
              className="rim-pulse relative rounded-2xl p-6 overflow-hidden"
              style={{
                background: "rgba(6,10,6,0.92)",
                border: "1px solid rgba(64,255,94,0.25)",
              }}
            >
              {/* Scanline */}
              <div
                className="scanline absolute inset-x-0 h-10 pointer-events-none"
                style={{
                  background: "linear-gradient(180deg, transparent, rgba(64,255,94,0.06), transparent)",
                  animationDelay: "0.8s",
                }}
              />
              <div className="text-[10px] font-mono mb-3 flex items-center gap-2"
                style={{ color: "rgba(64,255,94,0.5)" }}>
                <span className="ar-text inline-block w-1.5 h-1.5 rounded-full bg-current" />
                ROKID AR · 实时显示
              </div>
              {/* Translation row */}
              <div className="mb-4">
                <div className="text-[11px] font-mono mb-1" style={{ color: "rgba(64,255,94,0.45)" }}>TRANSLATION</div>
                <div className="ar-text text-base font-mono font-bold" style={{ color: "#40FF5E" }}>
                  "你好，很高兴认识你"
                </div>
                <div className="ar-text text-sm font-mono mt-0.5" style={{ color: "rgba(64,255,94,0.7)", animationDelay: "0.4s" }}>
                  "Nice to meet you too!"
                </div>
              </div>
              {/* Nav row */}
              <div className="border-t pt-3" style={{ borderColor: "rgba(64,255,94,0.1)" }}>
                <div className="text-[11px] font-mono mb-1" style={{ color: "rgba(64,255,94,0.45)" }}>NAVIGATION</div>
                <div className="ar-text flex items-center gap-2 text-sm font-mono font-semibold" style={{ color: "#40FF5E", animationDelay: "0.8s" }}>
                  <span className="text-lg">↗</span> 前方 80m 右转 · 西湖景区
                </div>
              </div>
            </div>

            {/* Mini cards row */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "提词进度", value: "第 3 / 12 页", icon: "🎤", delay: "0s" },
                { label: "AI 助理", value: "随时唤醒", icon: "🤖", delay: "1.2s" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rim-pulse rounded-xl p-4"
                  style={{
                    background: "rgba(6,10,6,0.92)",
                    border: "1px solid rgba(64,255,94,0.2)",
                    animationDelay: item.delay,
                  }}
                >
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <div className="text-[10px] font-mono mb-0.5" style={{ color: "rgba(64,255,94,0.45)" }}>{item.label}</div>
                  <div className="ar-text text-sm font-mono font-bold" style={{ color: "#40FF5E", animationDelay: item.delay }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Weight badge */}
            <div
              className="flex items-center gap-3 px-5 py-3 rounded-xl border"
              style={{
                background: "color-mix(in srgb, var(--primary) 6%, var(--card))",
                borderColor: "color-mix(in srgb, var(--primary) 20%, transparent)",
              }}
            >
              <span className="text-2xl">⚡</span>
              <div>
                <div className="text-xs text-muted-foreground">整机重量</div>
                <div className="font-bold text-foreground">仅 49g · 全天佩戴不压鼻</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Glasses cutout image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <img
            src={IMGS.glasses}
            alt="乐奇眼镜外观"
            className="mx-auto max-w-md w-full h-auto"
            style={{ filter: "drop-shadow(0 0 24px rgba(64,255,94,0.3))" }}
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  );
}

// ================================================
// FEATURES
// ================================================
function Features() {
  return (
    <Section id="features" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--primary) 10%, transparent)", color: "var(--primary)" }}
          >
            核心功能
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
            六大超能力，融进一副眼镜
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            不是玩具，是真正改变你日常的生产力工具
          </p>
        </motion.div>

        <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature) => (
            <motion.div
              key={feature.id}
              variants={fadeUp}
              whileHover={{ y: -6, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="relative rounded-2xl p-7 border border-border cursor-default group overflow-hidden"
              style={{
                background: "linear-gradient(145deg, var(--card), var(--muted))",
                boxShadow: "0 4px 24px color-mix(in srgb, var(--primary) 6%, transparent)",
              }}
            >
              <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${feature.color}`} />
              <div className="relative z-10">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--primary)" }}>
                  {feature.subtitle}
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{feature.desc}</p>
              </div>
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--primary) 25%, transparent)" }} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ================================================
// AI SCENES — 官方功能截图展示
// ================================================
const aiScenes = [
  { img: IMGS.ai1, label: "AI识物", desc: "精准识别，轻松认识世界" },
  { img: IMGS.ai3, label: "AI翻译", desc: "听懂所有语言，实时交流无障碍" },
  { img: IMGS.ai5, label: "实时导航", desc: "随时导航不迷路，想去哪就去哪" },
  { img: IMGS.ai2, label: "AI拍照答题", desc: "拍照即答，学习更高效" },
];

function AiScenes() {
  const [active, setActive] = useState(0);

  return (
    <section
      className="py-20 px-6 overflow-hidden"
      style={{ background: "var(--muted)" }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--accent)" }}
          >
            AI 能力
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">大事小事，乐奇都不是事</h2>
          <p className="text-muted-foreground text-lg">多模态 AI 大模型，助力每一个生活瞬间</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          {/* Tabs */}
          <div className="lg:col-span-2 flex flex-row lg:flex-col gap-3">
            {aiScenes.map((s, i) => (
              <motion.button
                key={i}
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                onClick={() => setActive(i)}
                className="text-left flex-1 lg:flex-none p-4 rounded-2xl border transition-all duration-300"
                style={{
                  background: active === i
                    ? "linear-gradient(135deg, color-mix(in srgb, var(--primary) 15%, transparent), color-mix(in srgb, var(--accent) 8%, transparent))"
                    : "var(--card)",
                  borderColor: active === i ? "var(--primary)" : "var(--border)",
                  boxShadow: active === i ? "0 8px 24px color-mix(in srgb, var(--primary) 22%, transparent)" : "none",
                }}
              >
                <div className="font-semibold text-sm text-foreground">{s.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5 hidden lg:block">{s.desc}</div>
              </motion.button>
            ))}
          </div>

          {/* Image */}
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-3 relative rounded-3xl overflow-hidden rim-pulse"
            style={{ border: "1px solid rgba(64,255,94,0.18)" }}
          >
            <div
              className="scanline absolute inset-x-0 h-16 pointer-events-none z-10"
              style={{ background: "linear-gradient(180deg, transparent, rgba(64,255,94,0.07), transparent)" }}
            />
            <img
              src={aiScenes[active].img}
              alt={aiScenes[active].label}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
            {/* Label overlay */}
            <div
              className="absolute bottom-0 inset-x-0 px-6 py-5"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}
            >
              <div className="ar-text text-sm font-mono font-semibold" style={{ color: "#40FF5E" }}>
                {aiScenes[active].label}
              </div>
              <div className="text-white/80 text-sm">{aiScenes[active].desc}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ================================================
// SCENARIOS
// ================================================
const scenarios = [
  { icon: "✈️", title: "海外旅行", desc: "语言不通？不慌。实时翻译字幕悬浮眼前，景点 AI 讲解，导航全程不迷路，拍下每个难忘瞬间。", highlight: "旅行者的最佳搭档" },
  { icon: "💼", title: "商务场合", desc: "国际会议中，对方说的你都能第一时间读到翻译。提词功能让你在台上自信满满，谈判不漏一字。", highlight: "职场加速器" },
  { icon: "🎓", title: "学习与教育", desc: "外语学习从此身临其境，演讲提词零学习成本，博物馆 AI 讲解让知识触手可及。", highlight: "学习新方式" },
  { icon: "🎬", title: "内容创作", desc: "第一视角记录生活，骑行 Vlog 就是一键的事。独特视角让你的内容与众不同，涨粉利器。", highlight: "创作者神器" },
];

function Scenarios() {
  const [active, setActive] = useState(0);
  return (
    <Section id="scenarios" className="py-24 px-6"
      style={{ background: "radial-gradient(ellipse 70% 50% at 20% 50%, color-mix(in srgb, var(--primary) 6%, transparent), transparent 60%), var(--background)" }}>
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--accent)" }}>
            使用场景
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">你的日常，它全都懂</h2>
          <p className="text-muted-foreground text-lg">从早到晚，从工作到生活，乐奇都在你身边</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div variants={staggerContainer} className="flex flex-col gap-4">
            {scenarios.map((s, i) => (
              <motion.button key={i} variants={fadeUp} onClick={() => setActive(i)} whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="text-left p-5 rounded-2xl border transition-all duration-300"
                style={{
                  background: active === i ? "linear-gradient(135deg, color-mix(in srgb, var(--primary) 12%, transparent), color-mix(in srgb, var(--accent) 6%, transparent))" : "var(--card)",
                  borderColor: active === i ? "var(--primary)" : "var(--border)",
                  boxShadow: active === i ? "0 8px 24px color-mix(in srgb, var(--primary) 20%, transparent)" : "none",
                }}>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl">{s.icon}</span>
                  <span className="font-bold text-foreground">{s.title}</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: active === i ? "var(--primary)" : "var(--muted-foreground)" }}>
                  {s.highlight}
                </span>
              </motion.button>
            ))}
          </motion.div>

          <motion.div key={active} variants={fadeUp}
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
            className="rounded-3xl p-8 md:p-12 flex flex-col justify-center"
            style={{
              background: "linear-gradient(145deg, var(--card), var(--muted))",
              border: "1px solid var(--border)",
              boxShadow: "0 20px 60px color-mix(in srgb, var(--primary) 12%, transparent)",
            }}>
            <div className="text-6xl mb-6">{scenarios[active].icon}</div>
            <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--primary)" }}>
              {scenarios[active].highlight}
            </div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-foreground mb-4">{scenarios[active].title}</h3>
            <p className="text-muted-foreground leading-relaxed text-base">{scenarios[active].desc}</p>
            <div className="mt-8 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: "var(--primary)", animation: "breathe 2.4s ease-in-out infinite" }} />
              <span className="text-sm text-muted-foreground">真实用户场景验证</span>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ================================================
// LIFESTYLE PHOTOS
// ================================================
function LifestyleGallery() {
  const imgs = [
    { src: IMGS.thumb1, label: "好相机" },
    { src: IMGS.thumb2, label: "随身快拍" },
    { src: IMGS.thumb3, label: "好助手" },
  ];
  return (
    <Section className="py-20 px-6" style={{ background: "var(--background)" }}>
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">不止一副好眼镜</h2>
          <p className="text-muted-foreground text-lg">好耳机 · 好相机 · 好助手 · 三合一</p>
        </motion.div>
        <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {imgs.map((item, i) => (
            <motion.div key={i} variants={fadeUp} whileHover={{ y: -6, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="relative rounded-2xl overflow-hidden group cursor-default"
              style={{ boxShadow: "0 8px 28px color-mix(in srgb, var(--primary) 10%, transparent)" }}>
              {/* Scanline on hover */}
              <div className="scanline absolute inset-x-0 h-16 pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "linear-gradient(180deg, transparent, rgba(64,255,94,0.08), transparent)" }} />
              <img src={item.src} alt={item.label} className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              {/* Overlay */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                style={{ background: "linear-gradient(to top, rgba(64,255,94,0.15), transparent)" }} />
              <div className="absolute bottom-0 inset-x-0 p-4"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.65), transparent)" }}>
                <div className="ar-text text-sm font-mono font-bold" style={{ color: "#40FF5E" }}>{item.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ================================================
// TESTIMONIALS
// ================================================
function Testimonials() {
  return (
    <Section id="testimonials" className="py-24 px-6" style={{ background: "var(--muted)" }}>
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--primary) 10%, transparent)", color: "var(--primary)" }}>
            真实评价
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">他们已经「看见了」未来</h2>
          <p className="text-muted-foreground text-lg">来自真实用户的使用反馈</p>
        </motion.div>
        <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TESTIMONIALS.map((t) => (
            <motion.div key={t.id} variants={fadeUp} whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="rounded-2xl p-6 border border-border"
              style={{
                background: "linear-gradient(145deg, var(--card), var(--background))",
                boxShadow: "0 4px 20px color-mix(in srgb, var(--primary) 6%, transparent)",
              }}>
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} size={14} className="fill-current" style={{ color: "var(--primary)" }} />
                ))}
              </div>
              <p className="text-foreground text-sm leading-relaxed mb-5 italic">"{t.content}"</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)" }}>
                  {t.avatar}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ================================================
// SPECS
// ================================================
function SpecsSection() {
  return (
    <Section id="specs" className="py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--primary) 10%, transparent)", color: "var(--primary)" }}>
            规格参数
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">技术实力，看得见</h2>
          <p className="text-muted-foreground text-lg">十年技术积累，200+次持续更新</p>
        </motion.div>

        {/* Product image */}
        <motion.div variants={fadeUp} className="mb-12 text-center">
          <img src={IMGS.shop} alt="乐奇眼镜门店" className="w-full max-w-2xl mx-auto rounded-2xl object-cover h-64"
            style={{ boxShadow: "0 20px 50px color-mix(in srgb, var(--primary) 15%, transparent)" }} loading="lazy" />
        </motion.div>

        <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SPECS.map((spec, i) => (
            <motion.div key={i} variants={fadeUp}
              className="flex items-center justify-between p-5 rounded-2xl border border-border group hover:border-primary transition-colors duration-200"
              style={{ background: "var(--card)" }}>
              <span className="text-muted-foreground text-sm font-medium">{spec.label}</span>
              <span className="font-bold text-sm font-mono group-hover:text-primary transition-colors duration-200 text-foreground">
                {spec.value}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust signals */}
        <motion.div variants={fadeUp} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: <ShieldCheck size={20} />, label: "1年质保" },
            { icon: <Globe2 size={20} />, label: "五大洲覆盖" },
            { icon: <Users size={20} />, label: "3万+开发者" },
            { icon: <Zap size={20} />, label: "核心功能永久免费" },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-border text-center"
              style={{ background: "var(--card)" }}>
              <span style={{ color: "var(--primary)" }}>{item.icon}</span>
              <span className="text-xs font-semibold text-foreground">{item.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ================================================
// CULTURE
// ================================================
const values = [
  {
    title: "玩乐精神",
    en: "Play",
    desc: "充满好奇心、探索欲，秉着好玩、自由的态度，敢于尝试，享受创新。",
    icon: "🎮",
  },
  {
    title: "极致品质",
    en: "Excellence",
    desc: "饱含敬畏之心、坚持工匠精神，让今天的自己比昨天更优秀。",
    icon: "✨",
  },
  {
    title: "开放分享",
    en: "Openness",
    desc: "以开放共赢的心态，传递知识和经验，分享给更多的伙伴。",
    icon: "🌐",
  },
  {
    title: "自我驱动",
    en: "Self-Drive",
    desc: "不断重塑的大脑，强烈的主动性；自律、自省、自燃。",
    icon: "🔥",
  },
  {
    title: "正直",
    en: "Integrity",
    desc: "诚实守信，直言不讳，表里如一。",
    icon: "🤝",
  },
];

function CultureSection() {
  return (
    <Section
      id="culture"
      className="py-24 px-6"
      style={{ background: "var(--background)" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div variants={fadeUp} className="text-center mb-16">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--primary) 10%, transparent)", color: "var(--primary)" }}
          >
            企业文化
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-5">
            我们为什么做这件事
          </h2>
          {/* Mission */}
          <div
            className="inline-block px-8 py-4 rounded-2xl mb-4 border"
            style={{
              background: "color-mix(in srgb, var(--primary) 7%, var(--card))",
              borderColor: "color-mix(in srgb, var(--primary) 22%, transparent)",
            }}
          >
            <div className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: "var(--primary)" }}>
              Mission · 使命
            </div>
            <div className="text-2xl md:text-3xl font-extrabold text-foreground">
              Leave Nobody Behind
            </div>
            <div className="text-lg font-semibold mt-1" style={{ color: "var(--primary)" }}>
              让每个人享受科技
            </div>
          </div>
        </motion.div>

        {/* Vision */}
        <motion.div variants={fadeUp} className="mb-14">
          <div className="text-center mb-6">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--muted-foreground)" }}>
              Vision · 愿景
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { num: "01", text: "全球交互体验的风向标公司" },
              { num: "02", text: "每天服务 10 亿用户" },
              { num: "03", text: "员工最有成就感的公司" },
            ].map((v) => (
              <motion.div
                key={v.num}
                variants={fadeUp}
                className="flex items-start gap-4 p-5 rounded-2xl border border-border"
                style={{ background: "var(--card)" }}
              >
                <span
                  className="text-3xl font-black leading-none flex-shrink-0 mt-0.5"
                  style={{
                    background: "linear-gradient(135deg, var(--primary), var(--accent))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {v.num}
                </span>
                <span className="text-base font-semibold text-foreground leading-snug">{v.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Values */}
        <motion.div variants={fadeUp} className="mb-3 text-center">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--muted-foreground)" }}>
            Value · 核心价值观
          </span>
        </motion.div>
        <motion.div
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          {values.map((v) => (
            <motion.div
              key={v.title}
              variants={fadeUp}
              whileHover={{ y: -5, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="relative rounded-2xl p-5 border border-border group overflow-hidden cursor-default"
              style={{
                background: "linear-gradient(145deg, var(--card), var(--muted))",
                boxShadow: "0 4px 20px color-mix(in srgb, var(--primary) 5%, transparent)",
              }}
            >
              {/* hover glow */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, transparent), transparent)" }} />
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                style={{ boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--primary) 28%, transparent)" }} />
              <div className="relative z-10">
                <div className="text-3xl mb-3">{v.icon}</div>
                <div
                  className="text-[10px] font-mono uppercase tracking-wider mb-1"
                  style={{ color: "var(--primary)" }}
                >
                  {v.en}
                </div>
                <div className="text-base font-bold text-foreground mb-2">{v.title}</div>
                <p className="text-xs text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ================================================
// BUY / CTA
// ================================================
function BuySection() {
  return (
    <section id="buy" className="py-24 px-6 text-center relative overflow-hidden"
      style={{ background: "radial-gradient(ellipse 80% 60% at 50% 50%, color-mix(in srgb, var(--primary) 12%, transparent), transparent 70%), var(--muted)" }}>
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />
      <div className="relative z-10 max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}>
          <span className="inline-block text-xs font-bold uppercase tracking-widest mb-4 px-3 py-1 rounded-full"
            style={{ background: "color-mix(in srgb, var(--primary) 10%, transparent)", color: "var(--primary)" }}>
            现在就开始
          </span>
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
            准备好让生活
            <br />
            <span style={{
              background: "linear-gradient(135deg, var(--primary), var(--accent))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              焕然一新了吗？
            </span>
          </h2>
          <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
            加入全球 10 万+用户，每天用乐奇眼镜感受 AI 魔法。
            <br />
            <strong className="text-foreground">1200+线下体验门店</strong>，先试戴再决定。
          </p>

          {/* Price card */}
          <div className="rim-pulse inline-block rounded-3xl px-10 py-8 mb-8 border"
            style={{
              background: "linear-gradient(145deg, var(--card), var(--background))",
              borderColor: "color-mix(in srgb, var(--primary) 25%, transparent)",
              boxShadow: "0 20px 60px color-mix(in srgb, var(--primary) 20%, transparent)",
            }}>
            <div className="text-muted-foreground text-sm mb-1">Rokid Glasses 乐奇 AI 眼镜</div>
            <div className="text-5xl font-black mb-2"
              style={{
                background: "linear-gradient(135deg, var(--primary), var(--accent))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
              ¥3299<span className="text-xl font-semibold">起</span>
            </div>
            <div className="text-xs text-muted-foreground">1年整机质保 · 核心 AI 功能永久免费</div>
            <div className="mt-3 flex items-center justify-center gap-2 px-4 py-2 rounded-xl border" style={{ borderColor: "color-mix(in srgb, var(--primary) 25%, transparent)", background: "color-mix(in srgb, var(--primary) 6%, transparent)" }}>
              <span className="text-base">🛡️</span>
              <div className="text-left">
                <div className="text-xs font-bold" style={{ color: "var(--primary)" }}>Rokid Care 碎屏险（可选购）</div>
                <div className="text-xs text-muted-foreground">联合蚂蚁保 × 众安保险 · 碎屏/进水全覆盖 · ¥199/年 或 ¥299/两年</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://glasses.rokid.com/" target="_blank" rel="noopener noreferrer"
              className="px-10 py-4 rounded-full font-bold text-base text-primary-foreground transition-all duration-300 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
                boxShadow: "0 8px 30px color-mix(in srgb, var(--primary) 45%, transparent)",
              }}>
              官网购买
            </a>
            <a href="https://www.jd.com/jiage/13202b7090f6c6c6f5e5.html" target="_blank" rel="noopener noreferrer"
              className="px-10 py-4 rounded-full font-bold text-sm border border-border hover:border-primary transition-all duration-200 text-foreground hover:text-primary">
              京东旗舰店
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ================================================
// PAGE
// ================================================
export default function Home() {
  return (
    <div className="dark text-foreground bg-background">
      <GlobalStyle />
      <Hero />
      <GlassesShowcase />
      <Features />
      <AiScenes />
      <Scenarios />
      <LifestyleGallery />
      <Testimonials />
      <SpecsSection />
      <CultureSection />
      <BuySection />
    </div>
  );
}
